import { useState, useEffect, useCallback, useRef } from 'react';

interface UseCertificatePreviewProps {
  htmlContent: string;
  isChangeDetected?: boolean;
}

export function useCertificatePreview({ 
  htmlContent,
  isChangeDetected = false
}: UseCertificatePreviewProps) {
  const [renderedHtml, setRenderedHtml] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(true);
  
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const hasRenderedRef = useRef<boolean>(false);
  const latestHtmlRef = useRef<string>('');
  
  // Process HTML with placeholder values for preview
  const processHtml = useCallback((html: string) => {
    if (!html) return '';
    
    try {
      // Replace template variables with example values
      let processed = html;
      
      const placeholders: Record<string, string> = {
        '{{student_name}}': 'John Smith',
        '{{course_name}}': 'Florida 4-Hour BDI Course',
        '{{completion_date}}': new Date().toLocaleDateString('en-US', { 
          year: 'numeric', 
          month: 'long', 
          day: 'numeric' 
        }),
        '{{certificate_number}}': 'CERT-123456',
        '{{citation_number}}': 'FL12345',
        '{{county}}': 'Miami-Dade County',
        '{{driver_license}}': 'A123-456-78-901-2',
        '{{dob}}': 'January 15, 1990',
        '{{course_reason}}': 'Court-Ordered'
      };
      
      Object.entries(placeholders).forEach(([key, value]) => {
        processed = processed.replace(new RegExp(key, 'g'), value);
      });
      
      return processed;
    } catch (error) {
      console.error('Error processing certificate HTML:', error);
      return `
        <div style="color: red; padding: 20px; font-family: sans-serif;">
          <h3>Error Processing Certificate</h3>
          <p>${error instanceof Error ? error.message : 'Unknown error'}</p>
        </div>
      `;
    }
  }, []);
  
  // Update preview with debouncing
  useEffect(() => {
    if (!htmlContent) return;
    
    // Skip if HTML hasn't changed
    if (htmlContent === latestHtmlRef.current && hasRenderedRef.current) {
      return;
    }
    
    latestHtmlRef.current = htmlContent;
    
    // Clear any existing debounce timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    
    // Set loading state unless we've already rendered once
    if (!hasRenderedRef.current) {
      setIsLoading(true);
    }
    
    // Debounce the update
    debounceTimerRef.current = setTimeout(() => {
      const processedHtml = processHtml(htmlContent);
      setRenderedHtml(processedHtml);
      hasRenderedRef.current = true;
      
      setTimeout(() => {
        setIsLoading(false);
      }, 100);
    }, isChangeDetected ? 150 : 100);
    
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [htmlContent, processHtml, isChangeDetected]);
  
  const handleIframeLoad = useCallback(() => {
    setIsLoading(false);
  }, []);
  
  return {
    renderedHtml,
    isLoading,
    handleIframeLoad,
  };
}
